<script setup lang="ts">
/**
 * One task, picked by typing part of its title: a box that doubles as the
 * filter, over a menu of the shared tasks that match, each with what it is
 * part of. Made for "which task does feedback go under" (FeedbackKeys.vue),
 * so it offers only unfinished shared tasks — of `teamId` when one is named,
 * since a sub-task is in its parent's team.
 */
import { computed, onUnmounted, ref, watch } from "vue";
import { useBoard } from "../stores/board.js";
import { swallowPress } from "../lib/dismiss.js";
import type { Task } from "@shared/types.js";

const props = defineProps<{
  modelValue: string | null;
  /** Only this team's tasks; null is any team the reader is in. */
  teamId?: string | null;
  placeholder?: string;
  disabled?: boolean;
}>();
const emit = defineEmits<{ "update:modelValue": [id: string | null] }>();

const board = useBoard();
const box = ref<HTMLElement | null>(null);
const open = ref(false);
const query = ref("");

/** Most shown at once: the box is for narrowing, not for scrolling a board. */
const SHOWN = 40;

const picked = computed(() => (props.modelValue ? (board.tasks.get(props.modelValue) ?? null) : null));

/** "Root › parent", so two tasks of one title can be told apart. */
function pathOf(t: Task): string {
  const names: string[] = [];
  for (let p = t.parentId ? board.tasks.get(t.parentId) : undefined; p; p = p.parentId ? board.tasks.get(p.parentId) : undefined) {
    names.unshift(p.title);
  }
  return names.join(" › ");
}

const matches = computed(() => {
  const q = query.value.trim().toLowerCase();
  const hits: Task[] = [];
  for (const t of board.tasks.values()) {
    if (t.privateTo !== null || t.status === "done" || board.isDraft(t.id)) continue;
    if (props.teamId && t.teamId !== props.teamId) continue;
    if (q && !t.title.toLowerCase().includes(q)) continue;
    hits.push(t);
  }
  // Roots first: a parent for feedback is nearly always one.
  hits.sort((a, b) => Number(a.parentId !== null) - Number(b.parentId !== null) || a.title.localeCompare(b.title));
  return hits;
});

/** What the closed box says: the picked task, or that it cannot be read here. */
const shown = computed(() =>
  open.value ? query.value : picked.value ? picked.value.title : props.modelValue ? (board.loaded ? "a task that has gone" : "…") : "",
);

function onDocPointerDown(ev: PointerEvent): void {
  if (box.value?.contains(ev.target as Node)) return;
  open.value = false;
  swallowPress(ev);
}

watch(open, (is) => {
  if (is) window.addEventListener("pointerdown", onDocPointerDown, true);
  else window.removeEventListener("pointerdown", onDocPointerDown, true);
});
onUnmounted(() => window.removeEventListener("pointerdown", onDocPointerDown, true));

function start(): void {
  if (props.disabled || open.value) return;
  query.value = "";
  open.value = true;
}

function choose(id: string | null): void {
  open.value = false;
  if (id !== props.modelValue) emit("update:modelValue", id);
}

function onEnter(): void {
  if (open.value && matches.value.length === 1) choose(matches.value[0]!.id);
}
</script>

<template>
  <div ref="box" class="picker task-picker">
    <input
      :value="shown"
      :placeholder="placeholder ?? 'no task'"
      :disabled="disabled"
      :title="picked ? [pathOf(picked), picked.title].filter(Boolean).join(' › ') : undefined"
      aria-label="Task"
      autocomplete="off"
      spellcheck="false"
      @focus="start"
      @click="start"
      @input="query = ($event.target as HTMLInputElement).value"
      @keydown.esc="open = false"
      @keydown.enter.prevent="onEnter"
    />
    <button
      v-if="modelValue"
      class="picker-btn"
      type="button"
      :disabled="disabled"
      title="No task: a root of its own"
      aria-label="Clear the task"
      @click="choose(null)"
    >×</button>

    <ul v-if="open" class="picker-menu">
      <li v-if="!board.loaded" class="picker-note">loading…</li>
      <li v-else-if="!matches.length" class="picker-note">no task matches</li>
      <li
        v-for="t in matches.slice(0, SHOWN)"
        :key="t.id"
        :class="{ current: t.id === modelValue }"
        :title="t.title"
        @click="choose(t.id)"
      >
        <span v-if="t.parentId" class="task-picker-path">{{ pathOf(t) }} › </span>{{ t.title }}
      </li>
      <li v-if="matches.length > SHOWN" class="picker-note">{{ matches.length - SHOWN }} more — type to narrow</li>
    </ul>
  </div>
</template>
